import { Helmet } from "react-helmet";

const BRAND = "BioClock-in";
const SITE_URL = "https://bioclock-in.com";

type SeoProps = {
  title?: string;
  description?: string;
  path?: string;              // e.g. "/"
  imagePath?: string;         // e.g. "/og-image.jpg"
};

export function Seo({
  title = "Offline Fingerprint Clock-In System | Mobile & Tablet Biometric Attendance",
  description = "Reliable offline fingerprint clock-in systems for mobile & tablets. Ideal for remote sites in construction, mining, farms & field teams.",
  path = "/",
  imagePath = "/og-image.png",
}: SeoProps) {
  const url = `${SITE_URL}${path}`;
  const image = `${SITE_URL}${imagePath}`;

  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Offline Fingerprint Clock-In System",
    brand: { "@type": "Brand", name: BRAND },
    description:
      "Biometric attendance that works offline on mobile and tablets with automatic sync.",
    category: "Biometric Time & Attendance",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "ZAR",
      lowPrice: "299",
      highPrice: "2999",
      offerCount: "3",
      availability: "https://schema.org/InStock",
      url,
    },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Does it work without internet?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. All clock-ins are stored on the device and sync automatically when a connection is available.",
        },
      },
      {
        "@type": "Question",
        name: "Which devices are supported?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Android phones, Android tablets, and iPad setups with supported fingerprint scanners.",
        },
      },
      {
        "@type": "Question",
        name: "Is it suitable for construction and mining?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. It’s designed for harsh, remote environments and supports kiosk mounts and long battery life.",
        },
      },
    ],
  };

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Biometric time & attendance",
    areaServed: { "@type": "Country", name: "Worldwide" },
    provider: {
      "@type": "Organization",
      name: BRAND,
      url: SITE_URL,
      logo: `${SITE_URL}/og-image.png`,
      // optional:
      // sameAs: ["https://www.facebook.com/yourpage", "https://www.linkedin.com/company/yourcompany"]
    },
    offers: { "@type": "Offer", priceCurrency: "ZAR", url: SITE_URL },
  };

  return (
    <Helmet>
      <html lang="en-ZA" />
      <title>{title}</title>
      <link rel="canonical" href={url} />
      <meta name="description" content={description} />

      {/* OpenGraph / Twitter */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(productLd)}</script>
      <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
    </Helmet>
  );
}
